/**
 * Dashboard widget registry.
 *
 * Checklist to add a widget:
 * 1. Create the component under `components/` (prefer shared WidgetShell / StatCounterGrid /
 *    StatusBreakdown / GenericListWidget; reuse existing domain SWR hooks — no new dashboard APIs).
 * 2. Export it from `components/index.ts`.
 * 3. Register here with id, name, category, defaultSize, defaultSettings, and `moduleGroupKey`
 *    for entitlement gating (skip core_reference / admin_settings packs).
 * 4. Optionally seed it in `createDefaultDashboardContent()` or a preset in useDashboardLayout.
 */
import React from 'react';
import type { ModuleGroupKey } from '../admin/module-management';
import {
  ScmsPipelineCounters,
  ScmsRecentDeliveries,
  ScmsRecentInvoices,
  ScmsDeliveryLineStatus,
  ScmsTopSuppliersBySpend,
  SparesPipelineCounters,
  SparesRecentDeliveries,
  SparesRecentInvoices,
  SparesDeliveryLineStatus,
  SparesTopSuppliersBySpend,
  ApprovalsInboxCounters,
  ApprovalsRecentPending,
  ApprovalsByStatus,
  DocumentsStatusCounters,
  DocumentsRecent,
  EformsCatalogCounters,
  EformsRecentSubmissions,
  EformsApprovalPending,
  EformsSubmissionStatusChart,
  EformsSubmissionsTrend,
  EformsFormWidgetHost,
  CrewPoolCounters,
  CrewRecent,
  CrewCertGaps,
  PmsDueCounters,
  PmsRecentWorkOrders,
  PmsDefectStatus,
  FinanceOpenItemsCounters,
  FinanceRecentJournals,
  FinancePeriodActivity,
  MgaPeriodCounters,
  MgaRecentPeriods,
  WrhComplianceCounters,
  WrhRecentSheets,
} from './components';

export type WidgetCategory = 'stats' | 'charts' | 'lists' | 'analytics' | 'calendar' | 'interactive';

export interface WidgetSettingsSchema {
  limit?: { type: 'number'; default: number; min?: number; max?: number };
  dateRange?: { type: 'string'; default: string };
  definitionId?: { type: 'number'; default: number; min?: number; max?: number };
  [key: string]: unknown;
}

/** Which dashboard filters (time range / scope) this widget respects */
export interface DashboardFilterSupport {
  timeRange?: boolean;
  scope?: boolean;
}

export interface WidgetDefinition {
  id: string;
  name: string;
  description: string;
  category: WidgetCategory;
  component: React.ComponentType<any>;
  defaultSize: { w: number; h: number };
  defaultSettings: Record<string, unknown>;
  settingsSchema?: WidgetSettingsSchema;
  minSize?: { w: number; h: number };
  maxSize?: { w: number; h: number };
  /** When set, this widget uses the dashboard Time range and/or Scope filters */
  dashboardFilterSupport?: DashboardFilterSupport;
  /** When true, multiple instances of this widget type can be added to one layout */
  allowMultiple?: boolean;
  /** Hide from Add Widget when this module pack is not entitled */
  moduleGroupKey?: ModuleGroupKey;
}

export const EFORMS_FORM_WIDGET_TYPE = 'eforms-form-widget';

const registry: WidgetDefinition[] = [
  // —— Purchasing · Supplies (shipping)
  {
    id: 'scms-pipeline-counters',
    name: 'Supplies · Pipeline Counters',
    description: 'Counts for requisitions, quotations, orders, deliveries and invoices',
    category: 'stats',
    component: ScmsPipelineCounters,
    defaultSize: { w: 12, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'shipping',
  },
  {
    id: 'scms-recent-deliveries',
    name: 'Supplies · Recent Deliveries',
    description: 'Latest supply deliveries',
    category: 'lists',
    component: ScmsRecentDeliveries,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'shipping',
  },
  {
    id: 'scms-recent-invoices',
    name: 'Supplies · Recent Invoices',
    description: 'Latest supply invoices',
    category: 'lists',
    component: ScmsRecentInvoices,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'shipping',
  },
  {
    id: 'scms-delivery-line-status',
    name: 'Supplies · Delivery Line Status',
    description: 'Breakdown of delivery line completion status',
    category: 'charts',
    component: ScmsDeliveryLineStatus,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'shipping',
  },
  {
    id: 'scms-top-suppliers-spend',
    name: 'Supplies · Top Suppliers by Spend',
    description: 'Suppliers ranked by purchase order spend',
    category: 'analytics',
    component: ScmsTopSuppliersBySpend,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 5 },
    settingsSchema: { limit: { type: 'number', default: 5, min: 1, max: 20 } },
    moduleGroupKey: 'shipping',
  },

  // —— Purchasing · Spares
  {
    id: 'spares-pipeline-counters',
    name: 'Spares · Pipeline Counters',
    description: 'Counts for spare requisitions through invoices',
    category: 'stats',
    component: SparesPipelineCounters,
    defaultSize: { w: 12, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'spares',
  },
  {
    id: 'spares-recent-deliveries',
    name: 'Spares · Recent Deliveries',
    description: 'Latest spare deliveries',
    category: 'lists',
    component: SparesRecentDeliveries,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'spares',
  },
  {
    id: 'spares-recent-invoices',
    name: 'Spares · Recent Invoices',
    description: 'Latest spare invoices',
    category: 'lists',
    component: SparesRecentInvoices,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'spares',
  },
  {
    id: 'spares-delivery-line-status',
    name: 'Spares · Delivery Line Status',
    description: 'Breakdown of spare delivery line completion',
    category: 'charts',
    component: SparesDeliveryLineStatus,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'spares',
  },
  {
    id: 'spares-top-suppliers-spend',
    name: 'Spares · Top Suppliers by Spend',
    description: 'Spare suppliers ranked by purchase order spend',
    category: 'analytics',
    component: SparesTopSuppliersBySpend,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 5 },
    settingsSchema: { limit: { type: 'number', default: 5, min: 1, max: 20 } },
    moduleGroupKey: 'spares',
  },

  // —— Approvals
  {
    id: 'approvals-inbox-counters',
    name: 'Approvals · Inbox Counters',
    description: 'My pending, submitted, and overdue approvals',
    category: 'stats',
    component: ApprovalsInboxCounters,
    defaultSize: { w: 12, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'approvals',
  },
  {
    id: 'approvals-recent-pending',
    name: 'Approvals · Recent Pending',
    description: 'Actionable pending approvals',
    category: 'lists',
    component: ApprovalsRecentPending,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'approvals',
  },
  {
    id: 'approvals-by-status',
    name: 'Approvals · By Status',
    description: 'Approval status breakdown',
    category: 'charts',
    component: ApprovalsByStatus,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'approvals',
  },

  // —— Documents
  {
    id: 'documents-status-counters',
    name: 'Documents · Status Counters',
    description: 'Draft, in approval, and approved document counts',
    category: 'stats',
    component: DocumentsStatusCounters,
    defaultSize: { w: 12, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'documents',
  },
  {
    id: 'documents-recent',
    name: 'Documents · Recent',
    description: 'Recently updated documents',
    category: 'lists',
    component: DocumentsRecent,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'documents',
  },

  // —— E-Forms
  {
    id: 'eforms-catalog-counters',
    name: 'E-Forms · Catalog Counters',
    description: 'Published forms and submission totals (or one form when scoped)',
    category: 'stats',
    component: EformsCatalogCounters,
    defaultSize: { w: 12, h: 2 },
    defaultSettings: {},
    settingsSchema: {
      definitionId: { type: 'number', default: 0, min: 0 },
    },
    moduleGroupKey: 'e_forms',
  },
  {
    id: 'eforms-recent-submissions',
    name: 'E-Forms · Recent Submissions',
    description: 'Latest submissions (optional form filter)',
    category: 'lists',
    component: EformsRecentSubmissions,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: {
      limit: { type: 'number', default: 6, min: 1, max: 50 },
      definitionId: { type: 'number', default: 0, min: 0 },
    },
    moduleGroupKey: 'e_forms',
  },
  {
    id: 'eforms-approval-pending',
    name: 'E-Forms · Approval Pending',
    description: 'E-form submissions awaiting approval',
    category: 'lists',
    component: EformsApprovalPending,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: {
      limit: { type: 'number', default: 6, min: 1, max: 50 },
      definitionId: { type: 'number', default: 0, min: 0 },
    },
    moduleGroupKey: 'e_forms',
  },
  {
    id: 'eforms-submission-status',
    name: 'E-Forms · Status Mix',
    description: 'Draft vs submitted mix for a form',
    category: 'charts',
    component: EformsSubmissionStatusChart,
    defaultSize: { w: 6, h: 3 },
    defaultSettings: {},
    settingsSchema: {
      definitionId: { type: 'number', default: 0, min: 0 },
    },
    moduleGroupKey: 'e_forms',
  },
  {
    id: 'eforms-submissions-trend',
    name: 'E-Forms · Submissions Trend',
    description: 'Daily submission volume for a form',
    category: 'charts',
    component: EformsSubmissionsTrend,
    defaultSize: { w: 6, h: 3 },
    defaultSettings: { days: 14, chartType: 'bar' },
    settingsSchema: {
      definitionId: { type: 'number', default: 0, min: 0 },
      days: { type: 'number', default: 14, min: 7, max: 60 },
    },
    moduleGroupKey: 'e_forms',
  },
  {
    id: EFORMS_FORM_WIDGET_TYPE,
    name: 'E-Forms · Form widget',
    description: 'A dashboard widget configured in Form Studio for a specific form',
    category: 'interactive',
    component: EformsFormWidgetHost,
    defaultSize: { w: 12, h: 4 },
    defaultSettings: { pageId: 0 },
    settingsSchema: { pageId: { type: 'number', default: 0, min: 0 } },
    minSize: { w: 4, h: 2 },
    allowMultiple: true,
    moduleGroupKey: 'e_forms',
  },

  // —— Crew
  {
    id: 'crew-pool-counters',
    name: 'Crew · Pool Counters',
    description: 'Onboard, available, and not-recommended counts',
    category: 'stats',
    component: CrewPoolCounters,
    defaultSize: { w: 12, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'crew',
  },
  {
    id: 'crew-recent',
    name: 'Crew · Recent',
    description: 'Recently updated crew files',
    category: 'lists',
    component: CrewRecent,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'crew',
  },
  {
    id: 'crew-cert-gaps',
    name: 'Crew · Compliance',
    description: 'Certificate and medical expiry snapshot',
    category: 'charts',
    component: CrewCertGaps,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'crew',
  },

  // —— PMS
  {
    id: 'pms-due-counters',
    name: 'PMS · Due Counters',
    description: 'Due, overdue, and open work orders',
    category: 'stats',
    component: PmsDueCounters,
    defaultSize: { w: 12, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'pms',
  },
  {
    id: 'pms-recent-work-orders',
    name: 'PMS · Recent Work Orders',
    description: 'Latest work orders',
    category: 'lists',
    component: PmsRecentWorkOrders,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'pms',
  },
  {
    id: 'pms-defect-status',
    name: 'PMS · Defect Status',
    description: 'Open defects by severity or status',
    category: 'charts',
    component: PmsDefectStatus,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'pms',
  },

  // —— Finance
  {
    id: 'finance-open-items-counters',
    name: 'Finance · Journal Counters',
    description: 'Draft, temp, and saved journal counts',
    category: 'stats',
    component: FinanceOpenItemsCounters,
    defaultSize: { w: 12, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'accounting',
  },
  {
    id: 'finance-recent-journals',
    name: 'Finance · Recent Journals',
    description: 'Latest accounting journals',
    category: 'lists',
    component: FinanceRecentJournals,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'accounting',
  },
  {
    id: 'finance-period-activity',
    name: 'Finance · Journal Status',
    description: 'Journal status mix',
    category: 'charts',
    component: FinancePeriodActivity,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'accounting',
  },

  // —— MGA
  {
    id: 'mga-period-counters',
    name: 'MGA · Period Counters',
    description: 'Open, draft, and closed MGA periods',
    category: 'stats',
    component: MgaPeriodCounters,
    defaultSize: { w: 12, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'mga',
  },
  {
    id: 'mga-recent-periods',
    name: 'MGA · Recent Periods',
    description: 'Latest MGA periods',
    category: 'lists',
    component: MgaRecentPeriods,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'mga',
  },

  // —— WRH
  {
    id: 'wrh-compliance-counters',
    name: 'WRH · Compliance Counters',
    description: 'Open days and non-compliance counts',
    category: 'stats',
    component: WrhComplianceCounters,
    defaultSize: { w: 12, h: 2 },
    defaultSettings: {},
    moduleGroupKey: 'wrh',
  },
  {
    id: 'wrh-recent-sheets',
    name: 'WRH · Recent Sheets',
    description: 'Recent work/rest hour days',
    category: 'lists',
    component: WrhRecentSheets,
    defaultSize: { w: 6, h: 2 },
    defaultSettings: { limit: 6 },
    settingsSchema: { limit: { type: 'number', default: 6, min: 1, max: 50 } },
    moduleGroupKey: 'wrh',
  },
];

export const getWidgetDefinition = (id: string): WidgetDefinition | undefined =>
  registry.find((w) => w.id === id);

export const getAllWidgetDefinitions = (): WidgetDefinition[] => [...registry];

export const getWidgetsByCategory = (category: WidgetCategory): WidgetDefinition[] =>
  registry.filter((w) => w.category === category);

/** Widgets that use the dashboard Time range and/or Scope filters */
export const getWidgetsUsingDashboardFilters = (): WidgetDefinition[] =>
  registry.filter((w) => w.dashboardFilterSupport && (w.dashboardFilterSupport.timeRange || w.dashboardFilterSupport.scope));

export const usesDashboardFilters = (widgetType: string): boolean => {
  const def = getWidgetDefinition(widgetType);
  return !!(def?.dashboardFilterSupport && (def.dashboardFilterSupport.timeRange || def.dashboardFilterSupport.scope));
};

export const getDefaultSettings = (widgetType: string): Record<string, unknown> => {
  const def = getWidgetDefinition(widgetType);
  return def ? { ...def.defaultSettings } : {};
};

export interface DashboardFiltersPayload {
  timeRange?: string;
  scope?: 'myWork' | 'team';
}

export const createWidgetElement = (
  widgetType: string,
  settings: Record<string, unknown>,
  isEditing?: boolean,
  dashboardFilters?: DashboardFiltersPayload | null
): React.ReactNode => {
  const def = getWidgetDefinition(widgetType);
  if (!def) return null;
  const merged = {
    ...def.defaultSettings,
    ...settings,
    isEditing,
    embedded: true,
    ...(dashboardFilters && {
      timeRange: dashboardFilters.timeRange,
      scope: dashboardFilters.scope,
    }),
  };
  const Component = def.component;
  return <Component {...merged} />;
};

export default registry;
